import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { List, OrderedSet } from 'immutable';
import { EventSearchFilter } from '../models/event-filter';
import { EventMenuSearchFilter } from '../models/event-menu-search-filter';
import { EventMenuSearchFilterMapper } from '../../menus/event-search-filter/event-menu-search-filter-mapper';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LogService } from '../../shared/log/log.service';
import { EventSearchParametersStore } from './event-search-parameters.store';
import { PioneerValidators } from '../../shared/utils/pioneer.validators';
import { DateUtil } from '../../shared/utils/date.util';
import { EventSearchFilterValidator } from '../../menus/event-search-filter/event-search-filter.validator';
import { EventSearchText } from 'app/event/models/event-search-text';

@Injectable()
export class EventSearchFormsStore {

  private eventSearchTextForm: FormGroup;
  private eventFilterForm: FormGroup;
  private filterRecord: EventMenuSearchFilter;
  private searchText: EventSearchText;

  private filterMenuErrorSubject: BehaviorSubject<any> = new BehaviorSubject(null);
  private filterEventMenuSearchFilterSubject: BehaviorSubject<EventMenuSearchFilter> = new BehaviorSubject(null);
  private filterSearchTextSubject: BehaviorSubject<EventSearchText> = new BehaviorSubject(null);


  constructor(private formBuilder: FormBuilder,
              private logger: LogService,
              private eventSearchParametersStore: EventSearchParametersStore,
              private eventMenuMapper: EventMenuSearchFilterMapper) {
    this.init();
  }

  /**
   * Both the searchFilterRecord and valueChanges subscriptions take the populated form and set the
   *   EventMenuSearchFilter business subject.
   *
   * This allows the store to correctly initialize the EventMenuSearchFilter subject from
   *   the EventSearchParametersStore OR the eventFilterForm FormGroup.
   */
  init() {
    this.logger.debug('EventSearchFormsStore.init');

    this.filterRecord = new EventMenuSearchFilter();
    this.searchText = new EventSearchText();
    this.populateSearchTextForm('');
    this.populateSearchFilterForm(this.filterRecord);

    this.eventSearchTextForm.valueChanges.debounceTime(250).subscribe(value => {
      this.logger.info(`EventSearchFormsStore: ngOnInit: eventSearchTextForm.valueChanges: ${JSON.stringify(value)}`);
      this.setFilterSearchText(new EventSearchText(value));
    });

    this.eventSearchParametersStore.searchFilterRecord.subscribe(searchFilterRecord => {

      // tslint:disable-next-line:max-line-length
      this.logger.info(`EventSearchFormsStore: ngOnInit: eventSearchParametersStore.searchFilterRecord: ${JSON.stringify(searchFilterRecord)}`);
      this.populateSearchFilterForm(searchFilterRecord);

      const formValue: any = this.eventFilterForm.value;
      this.formValueToEventMenuSearchFilter(formValue);
    });

    this.eventFilterForm.valueChanges.subscribe((formValue: any) => {
      this.logger.info(`EventSearchFormsStore: ngOnInit: eventFilterForm.valueChanges: ${JSON.stringify(formValue)}`);
      this.formValueToEventMenuSearchFilter(formValue);
    });

    this.eventFilterForm.statusChanges.subscribe(value => {
      this.setFilterMenuErrors(this.eventFilterForm.errors);
      this.logger.info(`EventSearchFormsStore: statusChanges: ${JSON.stringify(this.eventFilterForm.errors)} `);
    });
  }

  formValueToEventMenuSearchFilter(formValue: any) {
    const eventMenuSearchFilter = this.createFilterRecord(formValue);
    this.setFilterEventMenuSearchFilter(eventMenuSearchFilter);
    this.updatePaymentStatusGroup();
  }

  populateSearchFilterForm(filterRecord: EventMenuSearchFilter): void {
    this.logger.debug('EventSearchFormsStore.populateSearchFilterForm');
    if (!this.eventFilterForm) {
      this.eventFilterForm = this.formBuilder.group({
        'commissionId': [ filterRecord.commissionId ],
        'fundingReferenceId': [ filterRecord.fundingReferenceId ],
        'paymentReferenceId': [ filterRecord.paymentReferenceId ],
        'startDate': [ DateUtil.formatDate(filterRecord.startDate), [ PioneerValidators.isDate ] ],
        'endDate': [ DateUtil.formatDate(filterRecord.endDate), [ PioneerValidators.isDate ] ],
        'guestName': [ filterRecord.guestName ],
        'eventStatusMissingAgency': [ filterRecord.eventStatusMissingAgency ],
        'eventStatusCompleted': [ filterRecord.eventStatusCompleted ],
        'paymentStatus': this.formBuilder.group({
          'unpaid': [ filterRecord.paymentStatus.unpaid],
          'paid': [ filterRecord.paymentStatus.paid],
          'inProcess': [ filterRecord.paymentStatus.inProcess]
        })
      }, { validator: EventSearchFilterValidator.validate });
    } else {
      this.eventFilterForm.patchValue({
        'commissionId': filterRecord.commissionId,
        'fundingReferenceId': filterRecord.fundingReferenceId,
        'paymentReferenceId': filterRecord.paymentReferenceId,
        'startDate': DateUtil.formatDate(filterRecord.startDate),
        'endDate': DateUtil.formatDate(filterRecord.endDate),
        'guestName': filterRecord.guestName,
        'eventStatusMissingAgency': filterRecord.eventStatusMissingAgency,
        'eventStatusCompleted': filterRecord.eventStatusCompleted
      });

      this.eventFilterForm.get('paymentStatus').patchValue({
        'unpaid': filterRecord.paymentStatus.unpaid,
        'paid': filterRecord.paymentStatus.paid,
        'inProcess': filterRecord.paymentStatus.inProcess
      });
    }
  }

  populateSearchTextForm(searchText: string): void {
    if (!this.eventSearchTextForm) {
      this.eventSearchTextForm = this.formBuilder.group({
        'searchText': [searchText, [ Validators.required ]]
      });
    } else {
      this.eventSearchTextForm.patchValue({
        'searchText': searchText
      });
    }
  }

  get searchTextForm(): FormGroup {
    return this.eventSearchTextForm;
  }

  get filterForm(): FormGroup {
    return this.eventFilterForm;
  }


  searchFromSearchText(searchText: EventSearchText): void {
    const filterRecord = this.filterEventMenuSearchFilterSnapshot;

    if (filterRecord !== null) {
      const filterOptionsSet: OrderedSet<EventSearchFilter> = this.eventMenuMapper.toSearchFilterSet(filterRecord);
      this.eventSearchParametersStore.setFilterSet(filterOptionsSet);
    }

    this.eventSearchParametersStore.addSearchText(searchText.searchText);
    this.eventSearchParametersStore.updateUrl();
    this.populateSearchTextForm('');
  }


  searchFromFilter(record: EventMenuSearchFilter): void {
    this.filterRecord = record;

    const filterOptionsSet: OrderedSet<EventSearchFilter> = this.eventMenuMapper.toSearchFilterSet(this.filterRecord);
    this.eventSearchParametersStore.setFilterSet(filterOptionsSet);

    const filterText = this.filterSearchTextSnapshot;

    if (filterText && filterText.searchText) {
      this.eventSearchParametersStore.addSearchText(filterText.searchText);
      this.populateSearchTextForm('');
    }

    this.eventSearchParametersStore.updateUrl();
  }


  updatePaymentStatusGroup() {
    let paymentStatusGroup = this.eventFilterForm.get('paymentStatus');

    const unpaid = paymentStatusGroup.get('unpaid').value;
    const paid = paymentStatusGroup.get('paid').value;
    const inProcess = paymentStatusGroup.get('inProcess').value;

    const paymentArray: List<{ name: string; value: any }> = List.of(
      { name: 'unpaid', value: unpaid },
      { name: 'paid', value: paid },
      { name: 'inProcess', value: inProcess });

    // Returns only those records where the value is true
    const selectedOptionsList: List<{ name: string; value: any }> = paymentArray.filter(obj => obj.value === true).toList();

    this.logger.verbose(`updatePaymentStatusGroup: is Selected count: ${selectedOptionsList.size}`);

    if (selectedOptionsList.size === 1) {
      // The other 2 options have been unchecked, disable the last option so that it cannot be
      // unchecked
      if (paymentStatusGroup.get(selectedOptionsList.first().name).enabled) {
        paymentStatusGroup.get(selectedOptionsList.first().name).disable();
      }
    } else {
      paymentArray.forEach( opt => {
        if (paymentStatusGroup.get(opt.name).disabled) {
          paymentStatusGroup.get(opt.name).enable();
        }
      });
    }
  }

  get filterMenuErrors(): Observable<any> {
    return this.filterMenuErrorSubject.asObservable();
  }

  get filterMenuErrorsSnapshot(): any {
    return this.filterMenuErrorSubject.getValue();
  }

  setFilterMenuErrors(value: any): void {
    this.logger.debug(`EventSearchFormsStore.setFilterMenuErrors: ${JSON.stringify(value)}`);
    this.filterMenuErrorSubject.next(value);
  }

  get filterEventMenuSearchFilter(): Observable<EventMenuSearchFilter> {
    return this.filterEventMenuSearchFilterSubject.asObservable();
  }

  get filterEventMenuSearchFilterSnapshot(): EventMenuSearchFilter {
    return this.filterEventMenuSearchFilterSubject.getValue();
  }

  setFilterEventMenuSearchFilter(value: EventMenuSearchFilter): void {
    this.logger.debug(`EventSearchFormsStore.setFilterEventMenuSearchFilter: ${JSON.stringify(value)}`);
    this.filterEventMenuSearchFilterSubject.next(value);
  }

  get filterSearchText(): Observable<EventSearchText> {
    return this.filterSearchTextSubject.asObservable();
  }

  get filterSearchTextSnapshot(): EventSearchText {
    return this.filterSearchTextSubject.getValue();
  }

  setFilterSearchText(value: EventSearchText): void {
    this.logger.debug(`EventSearchFormsStore.setFilterSearchText: ${JSON.stringify(value)}`);
    this.filterSearchTextSubject.next(value);
  }

  createFilterRecord(model: any): EventMenuSearchFilter {
    this.logger.debug(`createFilterRecord: model: ${JSON.stringify(model)}`);

    /*
     * The last payment status can get disabled so we read the value directly from the form rather
     * than what comes in with the form submission for paymentStatus
     */
    let paymentStatusGroup = this.eventFilterForm.get('paymentStatus');

    const unpaid = paymentStatusGroup.get('unpaid').value;
    const paid = paymentStatusGroup.get('paid').value;
    const inProcess = paymentStatusGroup.get('inProcess').value;

    model.paymentStatus = {
      unpaid: unpaid,
      paid: paid,
      inProcess: inProcess
    };

    return new EventMenuSearchFilter(model);
  }


  isEventFilterOptionSelected(): boolean {

    const eventMenuSearchFilter = this.filterEventMenuSearchFilterSnapshot;

    if (eventMenuSearchFilter === null) {
      // this should be setup during the initialization of this store
      throw new Error('eventMenuSearchFilter can not be null');
    }

    this.logger.debug(`EventSearchFormsStore: isEventFilterOptionSelected: ${JSON.stringify(eventMenuSearchFilter)}`);

    const isExpanded = eventMenuSearchFilter.eventStatusCompleted ||
      eventMenuSearchFilter.eventStatusMissingAgency ||
      Boolean(eventMenuSearchFilter.startDate) ||
      Boolean(eventMenuSearchFilter.endDate) ||
      Boolean(eventMenuSearchFilter.guestName);

    this.logger.debug(`EventSearchFormsStore: isEventFilterOptionSelected: isExpanded ${JSON.stringify(isExpanded)}`);

    return isExpanded;
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/UAT-GroupPay/workspace/pioneer-web/src/app/event/service/event-search-forms.store.ts